import type { PvpAppearance } from '@kenocyber/shared';
import type { OpenWorldClientState } from './connection';

/**
 * **A trama da cena, traduzida do estado da ligação** (gate V28I.CF.1A).
 *
 * Tirada do `useOpenWorld` sem mudar um campo: o produto e a bancada do
 * Bombardeio de Profundidade empurram EXACTAMENTE a mesma trama pela mesma
 * ponte. Uma segunda cópia desta tradução seria uma segunda oportunidade de a
 * bancada mostrar outra coisa do que o produto mostra.
 */
export function openWorldSceneFrame(state: OpenWorldClientState) {
  return {
    me: state.me,
    sideMu: state.sideMu,
    entities: state.entities.map((entity) => ({
      id: entity.id,
      x: entity.x,
      y: entity.y,
      aimX: entity.aimX,
      aimY: entity.aimY,
      moveX: entity.moveX,
      moveY: entity.moveY,
      healthMilli: entity.healthMilli,
      maxHealthMilli: entity.maxHealthMilli,
      radiusMu: entity.radiusMu,
      appearance: (entity.appearance as PvpAppearance | null) ?? null,
      connected: entity.connected,
      ally: entity.ally,
      cards: entity.cards,
    })),
    /*
     * **A MESMA lista, e não uma cópia dela** (gate V28I.AJ).
     *
     * Isto construía um objeto por corpo por quadro só para reter cinco dos
     * campos que o corpo já tinha. Com quinze mil corpos em voo eram quinze
     * mil objetos vinte vezes por segundo, e nem um deles dizia nada de novo:
     * `OpenWorldShot` é um superconjunto estrutural de `OpenWorldSceneShot`,
     * com os mesmos nomes e os mesmos tipos.
     *
     * É seguro passar a referência porque estes objetos são **imutáveis por
     * construção**: nenhum caminho da ligação escreve num que já existe — a
     * entrada cria, uma atualização substitui, e o avanço linear reconstrói
     * com `{ ...shot, x, y }`. A cena só lê, e lê os mesmos campos.
     */
    shots: state.shots,
    /*
     * Efeitos desta trama, e só desta. A cena desenha-os uma vez; o estado
     * público das cartas diz a verdade sozinho no quadro seguinte.
     */
    cardBursts: state.cardBursts.map((burst) => ({
      id: burst.i,
      kind: burst.k,
      x: burst.x,
      y: burst.y,
      radiusMu: burst.r,
    })),
    blasts: state.blasts.map((blast) => ({
      id: blast.id,
      x: blast.x,
      y: blast.y,
      radiusMu: blast.r,
    })),
    summons: state.summons.map((summon) => ({
      id: summon.i,
      kind: summon.k,
      x: summon.x,
      y: summon.y,
      aimX: summon.ax,
      aimY: summon.ay,
      ownerId: summon.o,
      healthBps: summon.hp,
      pilotId: summon.pl ?? null,
      radiusMu: summon.r,
    })),
    /*
     * A proteção, como um facto e não como um relógio: a cena desenha
     * enquanto for verdade e apaga quando deixar de ser. Quem conta os vinte
     * segundos é o servidor, e ele repete-os uma vez por segundo.
     */
    protectedNow: (state.border?.p ?? 0) > 0,
    comets: state.comets,
    cometEvents: state.cometEvents.map((event) => ({
      id: event.i,
      ordinal: event.n,
      kind: event.k,
      part: event.p,
      x: event.x,
      y: event.y,
    })),
    planets: state.planets,
    planetEvents: state.planetEvents.map((event) => ({
      id: event.i,
      ordinal: event.n,
      kind: event.k,
      part: event.p,
      core: event.c,
      x: event.x,
      y: event.y,
    })),
    /*
     * Os mobs. A vida atravessa como decimal e nao como numero: ela e
     * `bigint` dos dois lados, e passa-la por `Number` seria perder digitos
     * numa progressao que nao tem teto.
     */
    mobs: state.mobs.map((mob) => ({
      id: mob.id,
      generation: mob.generation,
      mapIndex: mob.mapIndex,
      x: mob.x,
      y: mob.y,
      dirX: mob.dirX,
      dirY: mob.dirY,
      radiusMu: mob.radiusMu,
      healthMilli: mob.health.toString(10),
      maxHealthMilli: mob.maxHealth.toString(10),
      stance: mob.stance,
      poisoned: mob.poisoned,
      bleeding: mob.bleeding,
      cards: mob.cards,
    })),
    mobEvents: state.mobEvents.map((event) => ({
      id: event.i,
      ordinal: event.n,
      kind: event.k,
      x: event.x,
      y: event.y,
    })),
    bosses: state.bosses.map((boss) => ({
      id: boss.id,
      generation: boss.generation,
      mapIndex: boss.mapIndex,
      role: boss.role,
      scaleBps: boss.scaleBps,
      x: boss.x,
      y: boss.y,
      dirX: boss.dirX,
      dirY: boss.dirY,
      radiusMu: boss.radiusMu,
      healthMilli: boss.health.toString(10),
      maxHealthMilli: boss.maxHealth.toString(10),
      poisoned: boss.poisoned,
      bleeding: boss.bleeding,
      cards: boss.cards,
      /*
       * **A ultimate, tal como a autoridade a descreveu** (gate V28I.BG).
       *
       * `involved` é a resposta sobre MIM, e chega assim da sala: a lista de
       * envolvidos nunca viaja. O anel do Decreto vem em milli-unidades, e é
       * a banda que magoa — não uma aproximação dela.
       */
      ultimate:
        boss.ultimate === null
          ? null
          : {
              ability: boss.ultimate.k,
              phase: boss.ultimate.p,
              progressMilli: boss.ultimate.c,
              interruptMilli: boss.ultimate.ip,
              innerMu: boss.ultimate.ri,
              outerMu: boss.ultimate.ro,
              involved: boss.ultimate.iv,
            },
    })),
    /*
     * **Os golpes físicos em curso**, tal como a autoridade os descreveu.
     *
     * Só os bosses que estão a atacar entram: a lista é a projeção direta do
     * campo `s` de cada estado, sem nenhuma decisão do cliente pelo meio.
     */
    bossStrikes: state.bosses.map((boss) => ({
      id: boss.id,
      strike:
        boss.strike === null
          ? null
          : {
              ability: boss.strike.k,
              phase: boss.strike.p,
              progressMilli: boss.strike.c,
              stage: boss.strike.st,
              shape: boss.strike.sh,
              originX: boss.strike.ox,
              originY: boss.strike.oy,
              dirX: boss.strike.dx,
              dirY: boss.strike.dy,
              points: boss.strike.pt ?? [],
              reachMu: boss.strike.rr,
              innerMu: boss.strike.in,
              halfWidthMu: boss.strike.hw,
              sectorTanBps: boss.strike.an,
            },
    })),
    /*
     * **Os teleguiados e os núcleos**, projetados sem uma decisão pelo meio.
     *
     * Nem o dano nem o alvo de um teleguiado viajam, e por isso não estão
     * aqui: a cena desenha o corpo que a sala diz existir, e a perseguição já
     * diz de quem ele anda atrás.
     */
    bossHoming: state.bossHoming.map((one) => ({
      id: one.id,
      targetEntityId: one.targetEntityId,
      ability: one.ability,
      x: one.x,
      y: one.y,
      dirX: one.dirX,
      dirY: one.dirY,
      radiusMu: one.radiusMu,
    })),
    bossCores: state.bossCores.map((one) => ({
      id: one.id,
      bossId: one.bossId,
      x: one.x,
      y: one.y,
      radiusMu: one.radiusMu,
      healthMilli: one.health.toString(10),
      maxHealthMilli: one.maxHealth.toString(10),
    })),
    bossEmbers: state.embers.map((ember) => ({
      id: ember.id,
      x: ember.x,
      y: ember.y,
      radiusMu: ember.radiusMu,
      shape: ember.shape,
      dirX: ember.dirX,
      dirY: ember.dirY,
      sectorTanBps: ember.sectorTanBps,
      slowBps: ember.slowBps,
      bornAtTick: ember.bornAtTick,
      expiresAtTick: ember.expiresAtTick,
    })),
    tick: state.tick,
    receivedAt: state.receivedAt,
    ownSyncAt: state.ownSyncAt,
    bossEvents: state.bossEvents.map((event) => ({
      id: event.i,
      ordinal: event.n,
      kind: event.k,
      x: event.x,
      y: event.y,
      /* O raio autoritativo, quando o evento teve área. Ausente é ausente. */
      ...(event.r === undefined ? {} : { radiusMu: event.r }),
    })),
    waters: state.waters,
    merchant: state.merchant,
    boxes: state.boxes.map((box) => ({ id: box.i, x: box.x, y: box.y, ttl: box.ttl })),
    nearestBox: state.nearestBox,
    fishers: state.fishers.map((entry) => ({
      entityId: entry.e,
      areaId: entry.a,
      phase: entry.p,
      x: entry.x,
      y: entry.y,
    })),
    fishing:
      state.fishing === null
        ? null
        : {
            areaId: state.fishing.a,
            phase: state.fishing.p,
            /*
             * Milésimos, e a divisão é feita aqui uma vez: a cena desenha uma
             * barra, e não faz aritmética com `bigint` a sessenta quadros.
             */
            healthPermille:
              state.fishing.f === null || state.fishing.h === null
                ? null
                : Number((BigInt(state.fishing.h) * 1000n) / BigInt(state.fishing.f.hm)),
          },
  };
}

/** A assinatura que decide se uma trama é empurrada outra vez. */
export function openWorldSceneFrameSignature(
  state: OpenWorldClientState,
  payload: ReturnType<typeof openWorldSceneFrame>,
): string {
  /*
   * O ultimo evento entra na assinatura: dois empurroes podem trazer o mesmo
   * tique com estilhacos diferentes, e sem isto o segundo lote nao chegaria.
   */
  return `${state.tick}:${payload.protectedNow ? 1 : 0}:${
    state.cometEvents.at(-1)?.n ?? 0
  }:${state.planetEvents.at(-1)?.n ?? 0}:${state.mobEvents.at(-1)?.n ?? 0}:${state.bossEvents.at(-1)?.n ?? 0}:${state.fishing?.p ?? '-'}:${
    state.fishing?.h ?? '-'
  }:${state.waters.length}:${state.merchant === null ? '-' : 'm'}:${state.boxes.length}:${
    state.nearestBox ?? '-'
  }`;
}
